import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CheckoutView from "../components/CheckoutView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIVATE_CODES = {
  "campervan-3-nights-90": true,
};

const gbp = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(value || 0));

const AddOnPayment = () => {
  const { type } = useParams();

  const isPrivate = Boolean(type && PRIVATE_CODES[type]);

  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");

      try {
        if (isPrivate) {
          const { data } = await axios.get(
            `${API}/private-add-ons/${type}`
          );

          setEvent({
            name: data.eventName || "FERAL Fest",
            dateLine: data.dateLine || "",
          });

          setItems([
            {
              id: data.id,
              name: data.name,
              price: data.price,
              unit: data.unit,
              capacity: data.capacity,
              remaining: data.remaining,
              soldOut: data.soldOut,
              leftLabel: data.leftLabel,
              privateOnly: true,
            },
          ]);

          return;
        }

        const { data } = await axios.get(
          `${API}/events/feral-fest`
        );

        setEvent(data);

        const accommodation = data.accommodation || [];

        /*
         * Normal add-on page:
         * - Tent Camping
         * - Existing Campervan / Motorhome
         * - Any accommodation specifically marked Add-on only
         *
         * Private-only accommodation has already been removed
         * by the backend public API.
         */
        const publicAddOns = accommodation.filter((item) => {
          return (
            item.id === "acc-bell" ||
            item.id === "acc-campervan" ||
            item.addOnOnly === true
          );
        });

        setItems(publicAddOns);
      } catch (err) {
        setError(
          isPrivate
            ? "This private payment link is unavailable or has expired."
            : "We couldn't load the accommodation options. Please contact FERAL."
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [type, isPrivate]);

  const startCheckout = (item) => {
    if (!item) return;

    if (item.soldOut) {
      setError(
        "Sorry, this accommodation option is no longer available."
      );
      return;
    }

    if (
      typeof item.remaining === "number" &&
      item.remaining <= 0
    ) {
      setError(
        "Sorry, this accommodation option is no longer available."
      );
      return;
    }

    setError("");
    setSelected(item);
  };

  if (selected && event) {
    return (
      <CheckoutView
        event="feral-fest"
        items={[
          {
            id: selected.id,
            qty: 1,
          },
        ]}
        eventName={event.name || "FERAL Fest"}
        dateLine={
          isPrivate
            ? "FERAL Fest Weekend"
            : event.dateLine
        }
        lines={[
          {
            id: selected.id,
            name: selected.name,
            qty: 1,
            price: selected.price,
          },
        ]}
        total={selected.price}
        onBack={() => setSelected(null)}
      />
    );
  }

  return (
    <div className="bg-[#f2e5de] min-h-screen">
      <Navbar tone="sunset" />

      <section className="feral-texture min-h-[75vh] pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto">

          <div className="text-center mb-10">
            <p className="text-[#9b3d0a] tracking-[0.16em] uppercase text-xs font-semibold mb-3">
              FERAL Fest
            </p>

            <h1 className="feral-serif text-[#9b3d0a] text-4xl sm:text-5xl mb-4">
              {isPrivate
                ? "Private Accommodation Payment"
                : "Add Accommodation"}
            </h1>

            <p className="text-[#6e5646] max-w-xl mx-auto leading-relaxed">
              {isPrivate
                ? "Use this private link to complete your accommodation payment."
                : "For guests who already have their FERAL Fest weekend ticket and would like to add accommodation separately."}
            </p>
          </div>

          {error && (
            <div className="max-w-2xl mx-auto bg-[#f6eadb] border border-[#9b3d0a] text-[#9b3d0a] rounded-lg p-4 mb-6 text-center">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center text-[#6e5646]">
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center text-[#6e5646]">
              No accommodation options are currently available.
            </div>
          ) : (
            <div
              className={
                isPrivate
                  ? "max-w-xl mx-auto"
                  : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              }
            >
              {items.map((item) => {
                const soldOut = Boolean(item.soldOut);

                return (
                  <div
                    key={item.id}
                    className="bg-[#f6eadb] border border-[#d9c3a8] rounded-lg p-7 shadow-sm flex flex-col"
                  >
                    <div className="flex-1">
                      <p className="text-[#9b3d0a] uppercase tracking-[0.12em] text-xs font-semibold mb-3">
                        {isPrivate
                          ? "Private Accommodation"
                          : "Accommodation"}
                      </p>

                      <h2 className="feral-serif text-[#9b3d0a] text-3xl leading-tight mb-3">
                        {item.name}
                      </h2>

                      {item.desc && (
                        <p className="text-[#6e5646] text-sm mb-4">
                          {item.desc}
                        </p>
                      )}

                      {item.unit && (
                        <p className="text-[#9a876f] text-sm mb-3">
                          {item.unit}
                        </p>
                      )}

                      {item.leftLabel && !soldOut && (
                        <p className="text-[#9b3d0a] text-sm font-semibold mb-4">
                          {item.leftLabel}
                        </p>
                      )}
                    </div>

                    <div className="border-t border-[#d9c3a8] pt-5">
                      <p className="feral-serif text-[#9b3d0a] text-3xl mb-5">
                        {gbp(item.price)}
                      </p>

                      {soldOut ? (
                        <div className="w-full text-center bg-[#d8c7b2] text-[#796959] rounded-full py-3.5 font-semibold">
                          SOLD OUT
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startCheckout(item)}
                          className="w-full bg-[#9b3d0a] hover:bg-[#7e3208] text-[#f2e5de] py-3.5 px-6 font-semibold tracking-wide transition-colors"
                        >
                          {isPrivate
                            ? "CONTINUE TO PAYMENT"
                            : item.price === 36
                            ? "BOOK 1 NIGHT"
                            : item.id === "acc-campervan"
                            ? "BOOK CAMPERVAN PITCH"
                            : "BOOK ACCOMMODATION"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-xs text-[#9a876f] text-center mt-7">
            Secure payment via Stripe
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AddOnPayment;